import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Pregunta, OpcionPregunta } from '../model/encuesta.model';

@Injectable({
  providedIn: 'root'
})
export class PreguntasEncuestasService {

  private apiUrl = `${environment.apiUrl}/api/encuestas`;
  private preguntasUrl = `${environment.apiUrl}/api/preguntas-encuestas`;

  constructor(private http: HttpClient) {}

  /** Obtiene todas las preguntas de una encuesta */
  getPreguntasByEncuesta(encuestaId: number): Observable<Pregunta[]> {
    return this.http.get<Pregunta[]>(`${this.apiUrl}/${encuestaId}/preguntas`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  /** Obtiene preguntas sin autenticación (ruta pública) */
  getPreguntasPublicas(nit: string, encuestaId: number): Observable<Pregunta[]> {
    return this.http.get<Pregunta[]>(`${environment.apiUrl}/api/public/encuesta/${nit}/${encuestaId}/preguntas`).pipe(
      catchError(this.handleError)
    );
  }

  /** Obtiene una pregunta por su ID */
  getPreguntaById(encuestaId: number, preguntaId: number): Observable<Pregunta> {
    return this.http.get<Pregunta>(`${this.apiUrl}/${encuestaId}/preguntas/${preguntaId}`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  /** Crea una nueva pregunta en una encuesta */
  createPregunta(encuestaId: number, pregunta: Pregunta): Observable<Pregunta> {
    return this.http.post<Pregunta>(this.preguntasUrl, pregunta, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  /** Actualiza una pregunta existente */
  updatePregunta(preguntaId: number, pregunta: Pregunta): Observable<Pregunta> {
    return this.http.put<Pregunta>(`${this.preguntasUrl}/${preguntaId}`, pregunta, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  /** Elimina una pregunta */
  deletePregunta(encuestaId: number, preguntaId: number): Observable<any> {
    return this.http.delete(`${this.preguntasUrl}/${preguntaId}`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  /** Obtiene las opciones de una pregunta */
  getOpciones(encuestaId: number, preguntaId: number): Observable<OpcionPregunta[]> {
    return this.http.get<OpcionPregunta[]>(`${this.apiUrl}/${encuestaId}/preguntas/${preguntaId}/opciones`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  /** Obtiene las opciones de una pregunta sin autenticación (ruta pública) */
  getOpcionesPublicas(nit: string, encuestaId: number, preguntaId: number): Observable<OpcionPregunta[]> {
    return this.http.get<OpcionPregunta[]>(`${environment.apiUrl}/api/public/encuesta/${nit}/${encuestaId}/preguntas/${preguntaId}/opciones`).pipe(
      catchError(this.handleError)
    );
  }

  /** Agrega una opción a una pregunta */
  createOpcion(encuestaId: number, preguntaId: number, opcion: OpcionPregunta): Observable<OpcionPregunta> {
    return this.http.post<OpcionPregunta>(`${this.apiUrl}/${encuestaId}/preguntas/${preguntaId}/opciones`, opcion, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  /** Obtiene las opciones seleccionadas para una respuesta de selección múltiple */
  getOpcionesRespuesta(encuestaId: number, respuestaId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/${encuestaId}/respuestas/${respuestaId}/opciones`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  /** Elimina una opción de una pregunta */
  deleteOpcion(encuestaId: number, preguntaId: number, opcionId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${encuestaId}/preguntas/${preguntaId}/opciones/${opcionId}`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  private handleError(error: any): Observable<never> {
    const message = error?.error?.message || 'Error en la solicitud.';
    return throwError(() => new Error(message));
  }
}